import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

const FieldLabel = ({ children }) => (
  <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
    {children}
  </label>
);

const DEFAULTS = {
  title: "Footer",
  tagline: "The definitive guide to new luxury property in Finca Cortesín, Casares.",
  portal_text: "Independent property portal specializing in luxury homes and new developments in Finca Cortesín.",
  disclaimer: "Not affiliated with or endorsed by Finca Cortesín Resort.",
  contact_name: "Enova Estates",
  contact_address_line_1: "Manilva Beach Fase 2, Local 7",
  contact_address_line_2: "29692 Manilva, Málaga",
  contact_email: "bjorn@enovaestates.com",
  copyright: "© Enova Estates · livingfincacortesin.com",
  tagline_secondary: "Independent property advisory · Costa del Sol",
};

export default function AdminFooterEdit() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get("/admin/pages/footer", { params: { lang } });
        setPage({ ...DEFAULTS, ...data });
      } catch (_err) {
        setPage({ key: "footer", ...DEFAULTS });
      }
    })();
  }, [lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put("/admin/pages/footer", payload, { params: { lang } });
      toast.success("Footer saved");
    } catch (_err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const set = (k, v) => setPage({ ...page, [k]: v });

  return (
    <div className="p-10 lg:p-16 max-w-[900px]" data-testid="admin-footer-edit">
      <Link
        to="/admin/pages"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Footer
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        Footer text shown across every public page.
      </p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey={"footer"} disabled={false} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-10">
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Brand block</h2>
          <div>
            <FieldLabel>Tagline (large serif)</FieldLabel>
            <textarea
              value={page.tagline || ""}
              onChange={(e) => set("tagline", e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
              data-testid="footer-field-tagline"
            />
          </div>
          <div>
            <FieldLabel>Portal description</FieldLabel>
            <textarea
              value={page.portal_text || ""}
              onChange={(e) => set("portal_text", e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
              data-testid="footer-field-portal-text"
            />
          </div>
          <div>
            <FieldLabel>Disclaimer (italic)</FieldLabel>
            <textarea
              value={page.disclaimer || ""}
              onChange={(e) => set("disclaimer", e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
              data-testid="footer-field-disclaimer"
            />
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Contact block</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Company / contact name</FieldLabel>
              <input
                value={page.contact_name || ""}
                onChange={(e) => set("contact_name", e.target.value)}
                className={inputCls}
                data-testid="footer-field-contact-name"
              />
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#9A968D] mt-2">
                Always links to https://enovaestates.com
              </p>
            </div>
            <div>
              <FieldLabel>Contact email</FieldLabel>
              <input
                value={page.contact_email || ""}
                onChange={(e) => set("contact_email", e.target.value)}
                className={inputCls}
                data-testid="footer-field-contact-email"
              />
            </div>
            <div>
              <FieldLabel>Address line 1</FieldLabel>
              <input
                value={page.contact_address_line_1 || ""}
                onChange={(e) => set("contact_address_line_1", e.target.value)}
                className={inputCls}
                data-testid="footer-field-address-1"
              />
            </div>
            <div>
              <FieldLabel>Address line 2</FieldLabel>
              <input
                value={page.contact_address_line_2 || ""}
                onChange={(e) => set("contact_address_line_2", e.target.value)}
                className={inputCls}
                data-testid="footer-field-address-2"
              />
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Bottom bar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Copyright</FieldLabel>
              <input
                value={page.copyright || ""}
                onChange={(e) => set("copyright", e.target.value)}
                className={inputCls}
                data-testid="footer-field-copyright"
              />
            </div>
            <div>
              <FieldLabel>Secondary tagline</FieldLabel>
              <input
                value={page.tagline_secondary || ""}
                onChange={(e) => set("tagline_secondary", e.target.value)}
                className={inputCls}
                data-testid="footer-field-tagline-secondary"
              />
            </div>
          </div>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="footer-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save Footer"}
          </button>
        </div>
      </form>
    </div>
  );
}
